/**
 * 轻量 HTML 清洗 —— 用于渲染 CMS 返回的富文本。
 * 剥离脚本、样式、iframe 等可执行内容，以及内联事件属性和 javascript: 协议，
 * 避免 dangerouslySetInnerHTML 引入存储型 XSS。
 */

const BLOCKED_TAGS = ['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'form']

const ALLOWED_TAGS = new Set([
  'a', 'b', 'br', 'blockquote', 'caption', 'code', 'col', 'colgroup', 'dd', 'del', 'div', 'dl', 'dt',
  'em', 'figcaption', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p',
  'pre', 's', 'small', 'span', 'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th',
  'thead', 'tr', 'u', 'ul',
])

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/** 去掉 <!-- --> 注释，避免条件注释绕过 */
function stripComments(html) {
  return html.replace(/<!--[\s\S]*?-->/g, '')
}

/** 删除整个危险标签及其内容 */
function stripDangerousTags(html) {
  let out = html
  BLOCKED_TAGS.forEach((tag) => {
    out = out.replace(new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*?<\\/${tag}\\s*>`, 'gi'), '')
    out = out.replace(new RegExp(`<${tag}\\b[^>]*\\/?>`, 'gi'), '')
  })
  return out
}

/** 清理属性：剥离事件处理器与危险协议，a[href] 做协议白名单 */
function sanitizeAttributes(html) {
  return html.replace(/<([a-zA-Z][a-zA-Z0-9]*)((?:\s+[^<>]*?)?)\/?>/g, (match, tag, attrs) => {
    if (!ALLOWED_TAGS.has(tag.toLowerCase())) return ''

    const kept = []

    attrs.replace(/([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'>]+))/g,
      (_, name, _v, dq, sq, uq) => {
        const attr = name.toLowerCase()
        const value = dq ?? sq ?? uq ?? ''

        // 事件属性一律丢弃
        if (attr.startsWith('on')) return
        // style 可用于富文本排版，但禁止 expression/url 等
        if (attr === 'style') {
          if (/expression\s*\(|javascript:|url\s*\(/i.test(value)) return
          kept.push(`${name}="${escapeHtml(value)}"`)
          return
        }
        if (attr === 'href' || attr === 'src') {
          if (/^\s*(javascript|vbscript|data):/i.test(value.replace(/[\s ]/g, ''))) return
          kept.push(`${name}="${escapeHtml(value)}"`)
          return
        }
        if (attr === 'srcset') return
        if (['href', 'title', 'alt', 'width', 'height', 'colspan', 'rowspan', 'class', 'id'].includes(attr)) {
          kept.push(`${name}="${escapeHtml(value)}"`)
        }
      }
    )

    return kept.length ? `<${tag} ${kept.join(' ')}>` : `<${tag}>`
  })
}

/** 纯文本摘要，用于列表页 */
export function stripTags(html) {
  if (!html) return ''
  return String(html)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim()
}

export default function sanitize(html) {
  if (!html || typeof html !== 'string') return ''
  return sanitizeAttributes(stripDangerousTags(stripComments(html)))
}